import type { ReactNode } from 'react';
import {
  columnVisibilityFeature,
  flexRender,
  tableFeatures,
  type RowData,
  type Table,
} from '@tanstack/react-table';

// DataTable が getVisibleCells を使うため columnVisibilityFeature を登録する。呼び出し側もこの features を共有する (react-table v9 移行)
export const dataTableFeatures = tableFeatures({ columnVisibilityFeature });
import { cn } from '@/lib/utils';

type Props<TData extends RowData> = {
  table: Table<typeof dataTableFeatures, TData>;
  /** データ0件時のプレースホルダー */
  emptyState?: ReactNode;
  /** テーブル下部に表示するフッター（ページネーション等） */
  footer?: ReactNode;
  /** <table> 要素に追加する className（table-fixed 等） */
  tableClassName?: string;
  /** ローディング中はヘッダー + スケルトン行を表示する */
  isLoading?: boolean;
  /** スケルトン行数（isLoading 時のみ有効）。デフォルト 8 */
  skeletonRowCount?: number;
  /** 行クリック時のハンドラ（指定すると行全体が cursor-pointer + hover 色変化になる） */
  onRowClick?: (row: TData) => void;
  /** 選択中の行かどうか（選択中は背景をハイライトする） */
  getRowSelected?: (row: TData) => boolean;
  /** テーブル外枠の div に追加する className（rounded-none 等） */
  containerClassName?: string;
};

/**
 * 共通 DataTable コンポーネント（TanStack Table のヘッドレスAPIに薄いラッパー）
 *
 * 列固有の見た目調整は ColumnDef.meta.cellClassName / headerClassName で受け取る。
 * 例: { meta: { cellClassName: 'text-right tabular-nums' } }
 *
 * footer prop にページネーションなどを渡すと、テーブルと同じ罫線枠の中に
 * セクション分けされて表示される。
 */
export function DataTable<TData extends RowData>({
  table,
  emptyState,
  footer,
  tableClassName,
  isLoading = false,
  skeletonRowCount = 8,
  onRowClick,
  getRowSelected,
  containerClassName,
}: Props<TData>) {
  const rows = table.getRowModel().rows;
  const leafColumns = table.getAllLeafColumns();

  return (
    <div className={cn('overflow-hidden rounded-md', containerClassName)}>
      <div className="overflow-x-auto">
        <table className={cn('w-full border-collapse text-sm', tableClassName)}>
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id} className="border-b border-solid border-border bg-[#f6f6f6]">
                {headerGroup.headers.map((header) => {
                  const meta = header.column.columnDef.meta as
                    { headerClassName?: string } | undefined;
                  return (
                    <th
                      key={header.id}
                      className={cn(
                        'px-4 py-3 text-left text-sm font-bold whitespace-nowrap text-sub',
                        meta?.headerClassName
                      )}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(header.column.columnDef.header, header.getContext())}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody>
            {isLoading ? (
              Array.from({ length: skeletonRowCount }).map((_, rowIndex) => (
                <tr
                  key={`skeleton-${rowIndex}`}
                  className="border-b border-solid border-border bg-white"
                >
                  {leafColumns.map((column) => {
                    const meta = column.columnDef.meta as
                      { cellClassName?: string; skeletonHidden?: boolean } | undefined;
                    return (
                      <td key={column.id} className={cn('px-4 py-3', meta?.cellClassName)}>
                        {!meta?.skeletonHidden && (
                          <span className="block h-4.5 w-full animate-pulse rounded-full bg-gray_darken" />
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))
            ) : rows.length === 0 ? (
              <tr>
                <td
                  colSpan={table.getAllLeafColumns().length}
                  className="px-3 py-8 text-center text-sm text-sub"
                >
                  {emptyState ?? 'データがありません'}
                </td>
              </tr>
            ) : (
              rows.map((row) => {
                const isSubRow = row.depth > 0;
                const selected = getRowSelected?.(row.original) ?? false;
                return (
                  <tr
                    key={row.id}
                    onClick={onRowClick ? () => onRowClick(row.original) : undefined}
                    className={cn(
                      'border-b border-solid border-border text-sm text-sub',
                      selected ? 'bg-primary_lighten' : 'bg-white',
                      onRowClick && 'cursor-pointer',
                      onRowClick && !selected && 'hover:bg-gray',
                      // 子行はヘッダー(#f6f6f6)より薄めの背景にし、親行（白）との差別化を保ちつつ視認性を上げる
                      isSubRow && 'bg-[#fafafa] text-xs'
                    )}
                  >
                    {row.getVisibleCells().map((cell) => {
                      const meta = cell.column.columnDef.meta as
                        { cellClassName?: string } | undefined;
                      return (
                        <td
                          key={cell.id}
                          className={cn(
                            // 親行は padding を広めに取り、シェブロン有無で高さが変わらないよう統一
                            isSubRow ? 'px-4 py-1.5' : 'px-4 py-3',
                            meta?.cellClassName
                          )}
                        >
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      );
                    })}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
      {footer && (
        <div className="border-t border-solid border-border bg-white px-3 py-2">{footer}</div>
      )}
    </div>
  );
}
